import { alloyTheme } from './alloyTheme';
import {
  getBadgeTokensWeave,
  getBannerTokensWeave,
  getBreadcrumbsTokensWeave,
  getButtonTokens,
  getBoxComponentTokensWeave,
  getCheckboxTokensWeave,
  getColorPickerTokensWeave,
  getEmptyStateTokensWeave,
  getDropdownTokensWeave,
  getFooterTokensWeave,
  getFormFieldTokensWeave,
  getInlineEditTokensWeave,
  getMenuComponentTokensWeave,
  getModalComponentTokensWeave,
  getOverlayTokens,
  getPageHeaderTokensWeave,
  getPanelTokensWeave,
  getTextareaTokensWeave,
  getToggleTokensWeave,
  getTreeTokensWeave,
  getRadioButtonTokensWeave,
} from './componentTokens';
import { ALLOY_WEAVE_THEME_ID } from './constants';
import {
  ADesignTokens,
  BadgeDesignTokens,
  BreadcrumbsDesignTokens,
  ContentLoaderDesignTokens,
  EmptyStateDesignTokens,
  HighlightTextDesignTokens,
  InputDesignTokens,
  MenuDesignTokens,
  ModalDesignTokens,
  PanelDesignTokens,
  SearchFieldDesignTokens,
  AvatarDesignTokens,
  AlloyTypographyVariant,
  ButtonDesignTokens,
  CheckboxDesignTokens,
  ColorPickerDesignTokens,
  DropdownDesignTokens,
  FooterDesignTokens,
  FormFieldDesignTokens,
  InlineEditDesignTokens,
  PageHeaderDesignTokens,
  Font,
  OverlayDesignTokens,
  BoxDesignTokens,
  TabsDesignTokens,
  BannerDesignTokens,
  TextareaDesignTokens,
  ToggleDesignTokens,
  TreeDesignTokens,
  RadioButtonDesignTokens,
} from './types';
import { type WeaveTheme } from './types';

export type AlloyTheme = {
  tokens:
    | {
        component: {
          typography: Record<AlloyTypographyVariant, Font> & {
            'font-family': string;
          };
          a: ADesignTokens;
          accordion: {
            'margin-top'?: string;
            icon?: {
              'margin-left'?: string;
              size?: number;
              'transform-expanded'?: string;
              'transform-collapsed'?: string;
            };
            secondary: {
              'text-color'?: string;
            };
            label?: {
              'margin-top'?: string;
              'margin-bottom'?: string;
              'margin-left'?: string;
            };
          };
          contentloader?: ContentLoaderDesignTokens;
          divider?: {
            color?: {
              subtle?: string;
              default?: string;
              strong?: string;
            };
          };
          emptystate?: EmptyStateDesignTokens;
          avatar: AvatarDesignTokens;
          highlighttext?: HighlightTextDesignTokens;
          input: InputDesignTokens;
          searchfield: SearchFieldDesignTokens;
          button?: ButtonDesignTokens;
          menu?: MenuDesignTokens;
          modal?: ModalDesignTokens;
          panel?: PanelDesignTokens;
          overlay?: OverlayDesignTokens;
          box?: BoxDesignTokens;
          tabs?: TabsDesignTokens;
          banner?: BannerDesignTokens;
          checkbox?: CheckboxDesignTokens;
          radioButton?: RadioButtonDesignTokens;
          textarea?: TextareaDesignTokens;
          colorPicker?: ColorPickerDesignTokens;
          formField?: FormFieldDesignTokens;
          inlineEdit?: InlineEditDesignTokens;
          dropdown?: DropdownDesignTokens;
          footer?: FooterDesignTokens;
          pageHeader?: PageHeaderDesignTokens;
          toggle?: ToggleDesignTokens;
          tree?: TreeDesignTokens;
          badge?: BadgeDesignTokens;
          breadcrumbs?: BreadcrumbsDesignTokens;
        };
      }
    | undefined;
};

const fontFamily = 'Artifakt Element, sans-serif';

export const getAccWeaveTheme = (weaveTheme: WeaveTheme): AlloyTheme => {
  const tokens = weaveTheme.tokens;
  const highDensityTokens = weaveTheme.highDensityTokens;
  if (!tokens) {
    // Weave theme hasn't been loaded yet, so we can't get the tokens
    // so provide a weave theme compatible version of the alloy theme here
    // this will make use of the acc theme we will provide as a weave theme
    // this is to ensure we are backwards compatible and not forcing users to wrap
    // components with WeaveThemeProvider
    return alloyTheme as unknown as AlloyTheme;
  }

  const result = {
    ...weaveTheme,
    tokens: {
      ...tokens,
      component: {
        ...tokens?.component,
        typography: {
          ...tokens?.component?.typography,
          'font-family': fontFamily,
          hero: {
            fontFamily,
            fontWeight: 400,
            fontSize: tokens?.generic['font-size'][48],
            lineHeight: '64px',
          },
          title: {
            fontFamily,
            fontWeight: 400,
            fontSize: tokens?.generic['font-size'][30],
            lineHeight: '42px',
          },
          heading1: {
            fontFamily,
            fontWeight: 400,
            fontSize: tokens?.generic['font-size'][24],
            lineHeight: tokens?.generic['line-height'][30],
          },
          heading2: {
            fontFamily,
            fontWeight: 400,
            fontSize: tokens?.generic['font-size'][20],
            lineHeight: tokens?.generic['line-height'][26],
          },
          heading3: {
            fontFamily,
            fontWeight: 600,
            fontSize: tokens?.generic['font-size'][16],
            lineHeight: tokens?.generic['line-height'][22],
          },
          heading4: {
            fontFamily,
            fontWeight: 700,
            fontSize: tokens?.generic['font-size'][14],
            lineHeight: tokens?.generic['line-height'][20],
          },
          heading5: {
            fontFamily,
            fontWeight: 700,
            fontSize: tokens?.generic['font-size'][12],
            lineHeight: tokens?.generic['line-height'][16],
          },
          bodyLarge: {
            fontFamily,
            fontWeight: 500,
            fontSize: tokens?.generic['font-size'][16],
            lineHeight: tokens?.generic['line-height'][22],
          },
          bodyLargeBold: {
            fontFamily,
            fontWeight: 700,
            fontSize: tokens?.generic['font-size'][16],
            lineHeight: tokens?.generic['line-height'][22],
          },
          bodyMedium: {
            fontFamily,
            fontWeight: 500,
            fontSize: tokens?.generic['font-size'][14],
            lineHeight: tokens?.generic['line-height'][20],
          },
          bodyMediumBold: {
            fontFamily,
            fontWeight: 700,
            fontSize: tokens?.generic['font-size'][14],
            lineHeight: tokens?.generic['line-height'][20],
          },
          bodySmall: {
            fontFamily,
            fontWeight: 500,
            fontSize: tokens?.generic['font-size'][12],
            lineHeight: tokens?.generic['line-height'][16],
          },
          bodySmallBold: {
            fontFamily,
            fontWeight: 700,
            fontSize: tokens?.generic['font-size'][12],
            lineHeight: tokens?.generic['line-height'][16],
          },
          labelMedium: {
            fontFamily,
            fontWeight: 600,
            fontSize: tokens?.generic['font-size'][14],
            lineHeight: tokens?.generic['line-height'][20],
          },
          labelSmall: {
            fontFamily,
            fontWeight: 600,
            fontSize: tokens?.generic['font-size'][12],
            lineHeight: tokens?.generic['line-height'][16],
          },
          caption: {
            fontFamily,
            fontWeight: 500,
            fontSize: tokens?.generic['font-size'][11],
            lineHeight: '15px',
          },
          captionBold: {
            fontFamily,
            fontWeight: 700,
            fontSize: tokens?.generic['font-size'][11],
            lineHeight: '15px',
          },
          eyebrow: {
            fontFamily,
            fontWeight: 700,
            fontSize: '10px',
            lineHeight: '15px',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          },
          ellipsis: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          lineClamp: {
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          },
        },
        a: {
          'text-color': {
            hover: tokens?.component?.textlink?.primary?.['text-color'],
            default: tokens?.component?.textlink?.primary?.['text-color'],
            pressed: tokens?.component?.textlink?.primary?.['text-color'],
          },
          'text-decoration': {
            default: 'none',
            hover: 'underline',
          },
          'text-decoration-thickness': {
            default:
              tokens?.component?.textlink?.['text-decoration-thickness']
                ?.default,
          },
          'box-shadow': {
            focus: `${tokens?.component?.textlink?.['box-shadow-color']?.focus} ${tokens?.component?.textlink?.['box-shadow-x']?.focus} ${tokens?.component?.textlink?.['box-shadow-y']?.focus} ${tokens?.component?.textlink?.['box-shadow-blur']?.focus} ${tokens?.component?.textlink?.['box-shadow-spread']?.focus}`,
          },
        },
        accordion: {
          ...tokens?.component?.accordion,
          'font-family': fontFamily,
          'margin-top': '0',
          icon: {
            ...tokens?.component?.accordion?.icon,
            size: 10,
            'margin-left': '0',
            'transform-expanded': 'rotate(90deg)',
            'transform-collapsed': 'rotate(0deg)',
          },
          secondary: {
            'text-color': tokens?.semantic?.['text-color']?.light,
          },
          label: {
            'margin-top':
              tokens?.component?.accordion?.['indicator-left']?.label?.[
                'padding-top'
              ],
            'margin-bottom':
              tokens?.component?.accordion?.['indicator-left']?.label?.[
                'padding-bottom'
              ],
            'margin-left':
              tokens?.component?.accordion?.['indicator-left']?.label?.[
                'padding-left'
              ],
          },
        },
        contentloader: {
          'background-color': tokens?.component?.skeleton?.['background-color'],
          'foreground-color':
            tokens?.component?.skeleton?.highlight?.['background-color'],
        },
        divider: {
          ...tokens?.component?.divider,
          color: {
            subtle: tokens?.semantic?.['border-color']?.divider?.light,
            default: tokens?.semantic?.['border-color']?.divider?.light,
            strong: tokens?.semantic?.['border-color']?.divider?.heavy,
          },
        },
        avatar: {
          ...tokens?.component?.avatar,
          'font-family': fontFamily,
          'font-weight': '400',
          border: {
            color: tokens?.component?.avatarbundle?.['border-color'],
          },
          default: {
            'icon-color': tokens?.semantic?.['icon-color']?.medium,
            'background-color':
              tokens?.component?.avatarbundle?.truncate?.['background-color'],
          },
        },
        highlighttext: {
          'background-color':
            tokens?.component?.input?.['background-color']?.selected,
        },
        input: {
          ...tokens?.component?.input,
          'font-family': fontFamily,
          disabled: {
            'background-color': tokens?.generic.color.charcoal[100][100],
            'text-color': tokens?.generic.color.charcoal[500][100],
            'border-color': tokens?.generic.color.charcoal[200][100],
          },
        },
        searchfield: {
          icon: {
            color: tokens?.semantic?.['icon-color']?.medium,
          },
          clearButton: {
            icon: {
              fill: tokens?.semantic?.['icon-color']?.medium,
            },
          },
          rightAdornment: {
            gap: tokens?.generic?.spacing?.xs,
          },
        },
        menu: getMenuComponentTokensWeave(tokens, fontFamily),
        modal: getModalComponentTokensWeave(tokens, fontFamily),
        panel: getPanelTokensWeave(tokens, fontFamily),
        progressring: {
          ...tokens?.component?.progressring,
          'icon-color': {
            default: tokens?.component?.progressring?.['dark-ring']?.fill,
            inverse:
              tokens?.component?.progressring?.['solid-contrast']?.['dark-ring']
                ?.fill,
          },
          'icon-color-light': {
            default: tokens?.component?.progressring?.['light-ring']?.fill,
            inverse:
              tokens?.component?.progressring?.['solid-contrast']?.[
                'light-ring'
              ]?.fill,
          },
        },
        button: getButtonTokens(tokens, highDensityTokens),
        emptystate: getEmptyStateTokensWeave(tokens, fontFamily),
        overlay: getOverlayTokens(tokens),
        tabs: {
          'font-family': fontFamily,
          'font-weight': tokens?.component?.tab?.['font-weight']?.default,
          'font-size': tokens?.component?.tab?.['font-size'],
          'line-height': tokens?.component?.tab?.['line-height'],

          'margin-horizontal':
            tokens?.component?.tab?.underline?.['margin-right'],
          'margin-bottom': '-1px',
          'padding-vertical':
            tokens?.component?.tab?.underline?.['padding-top'],
          'padding-horizontal':
            tokens?.component?.tab?.underline?.['padding-left'],
          'padding-bottom': `calc(${tokens?.component?.tab?.underline?.['padding-top']} + 1px)`,
          'icon-margin': tokens?.generic?.spacing?.[8],
          'min-width': '48px',

          'border-bottom': `${tokens?.component?.tab?.underline?.['border-width']?.selected} solid transparent`,

          default: {
            'text-color': tokens?.component?.tab?.['text-color'],
            'icon-color': tokens?.component?.tab?.icon?.fill,
          },

          hover: {
            'text-color': tokens?.component?.tab?.['text-color'],
            'icon-color': tokens?.component?.tab?.icon?.fill,
            'border-color':
              tokens?.component?.tab?.underline?.['border-color']?.hover,
          },

          focus: {
            'text-color': tokens?.component?.tab?.['text-color'],
            'icon-color': tokens?.component?.tab?.icon?.fill,
            'border-color':
              tokens?.component?.tab?.underline?.['border-color']?.selected,
          },

          active: {
            'text-color': tokens?.component?.tab?.['text-color'],
            'icon-color': tokens?.component?.tab?.icon?.fill,
            'font-weight': tokens?.component?.tab?.['font-weight']?.selected,
            'border-color':
              tokens?.component?.tab?.underline?.['border-color']?.selected,
          },

          disabled: {
            'text-color': tokens?.component?.tab?.['text-color'],
            'icon-color': tokens?.component?.tab?.icon?.fill,
          },

          indicator: {
            'border-color':
              tokens?.component?.tab?.underline?.['border-color']?.selected,
            'border-width':
              tokens?.component?.tab?.underline?.['border-width']?.selected,
          },

          container: {
            'border-bottom-color':
              tokens?.component?.tab?.underline?.['border-bottom-color'],
            'border-bottom-width':
              tokens?.component?.tab?.underline?.['border-bottom-width'],
          },

          'focus-shadow': {
            color:
              tokens?.component?.tab?.underline?.['box-shadow-color']?.focus,
            width: '0px',
            offset: tokens?.component?.tab?.underline?.['box-shadow-y']?.focus,
            blur: tokens?.component?.tab?.underline?.['box-shadow-blur']?.focus,
            spread:
              tokens?.component?.tab?.underline?.['box-shadow-spread']?.focus,
          },

          opacity: {
            disabled: tokens?.component?.tab?.opacity?.disabled,
          },
        },
        banner: getBannerTokensWeave(tokens, fontFamily),
        textarea: getTextareaTokensWeave(tokens, fontFamily),
        box: getBoxComponentTokensWeave(tokens),
        checkbox: getCheckboxTokensWeave(tokens),
        radioButton: getRadioButtonTokensWeave(tokens),
        colorPicker: getColorPickerTokensWeave(tokens, fontFamily),
        formField: getFormFieldTokensWeave(tokens, fontFamily),
        inlineEdit: getInlineEditTokensWeave(tokens, fontFamily),
        dropdown: getDropdownTokensWeave(tokens, fontFamily),
        footer: getFooterTokensWeave(tokens, fontFamily),
        pageHeader: getPageHeaderTokensWeave(tokens, fontFamily),
        toggle: getToggleTokensWeave(tokens),
        tree: getTreeTokensWeave(tokens, fontFamily),
        badge: getBadgeTokensWeave(tokens, fontFamily),
        breadcrumbs: getBreadcrumbsTokensWeave(tokens, fontFamily),
      },
    },
    id: ALLOY_WEAVE_THEME_ID,
  };

  return result as unknown as AlloyTheme;
};
